import type { CSSProperties, ReactNode } from 'react';

/**
 * Single-stroke geometric icons on a 24px grid — no fills, no emoji.
 * The set is deliberately small; if a screen needs a new glyph, draw it here
 * so the whole product keeps one hand.
 */

export interface IconProps {
  size?: number;
  sw?: number;
  style?: CSSProperties;
  title?: string;
}

function Svg({ children, size = 18, sw = 1.8, style, title }: IconProps & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title}
    >
      {title ? <title>{title}</title> : null}
      {children}
    </svg>
  );
}

const paths = {
  clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3.5 2" /></>,
  play: <path d="M8 6l10 6-10 6z" />,
  stop: <rect x="7" y="7" width="10" height="10" rx="1" />,
  calendar: <><rect x="4" y="5" width="16" height="16" rx="2" /><path d="M4 10h16M8 3v4M16 3v4" /></>,
  doc: <><path d="M6 3h8l4 4v14H6z" /><path d="M14 3v4h4M9 12h6M9 16h6" /></>,
  inbox: <><path d="M4 13h4l2 3h4l2-3h4" /><path d="M4 13V6a1 1 0 011-1h14a1 1 0 011 1v7" /><path d="M4 13v5a1 1 0 001 1h14a1 1 0 001-1v-5" /></>,
  globe: <><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c3 3.5 3 14 0 18M12 3c-3 3.5-3 14 0 18" /></>,
  download: <path d="M12 4v11m-5-4l5 5 5-5M5 20h14" />,
  edit: <path d="M4 20l1-4L16.5 4.5a2.1 2.1 0 013 3L8 19z" />,
  user: <><circle cx="12" cy="8" r="4" /><path d="M4 21c1.5-4 5-5.5 8-5.5s6.5 1.5 8 5.5" /></>,
  pin: <><path d="M12 21s-6.5-6-6.5-11a6.5 6.5 0 0113 0c0 5-6.5 11-6.5 11z" /><circle cx="12" cy="10" r="2.2" /></>,
  sign: <path d="M3 17c2-4 4-6 5-5s-1 4 0 5 3-1 4-4 2-2 2 0 1 2 3 1 3-2 4-2" />,
  plus: <path d="M12 5v14M5 12h14" />,
  chart: <path d="M4 20V10M10 20V4M16 20v-8M21 20H3" />,
  lock: <><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V7a4 4 0 018 0v4" /></>,
  unlock: <><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V7a4 4 0 017.8-1.2" /></>,
  info: <><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 7.5v.5" /></>,
  settings: <><circle cx="12" cy="12" r="3" /><path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1" /></>,
  phone: <path d="M8 3H5a2 2 0 00-2 2c0 9 7 16 16 16a2 2 0 002-2v-3l-4.5-2-2.5 2.5A15 15 0 019.5 10L12 7.5z" />,
  alert: <><path d="M12 4l9 16H3z" /><path d="M12 10v4M12 17v.5" /></>,
  check: <path d="M4 12.5l5 5L20 6.5" />,
  arrowLeft: <path d="M15 5l-7 7 7 7" />,
  arrowRight: <path d="M9 5l7 7-7 7" />,
  logout: <><path d="M15 4h3a2 2 0 012 2v12a2 2 0 01-2 2h-3" /><path d="M10 8l-4 4 4 4M6 12h10" /></>,
  refresh: <><path d="M20 12a8 8 0 11-2.3-5.6" /><path d="M20 4v5h-5" /></>,
} as const;

export type IconName = keyof typeof paths;

export const Ic = Object.fromEntries(
  (Object.keys(paths) as IconName[]).map((name) => [
    name,
    (props: IconProps) => <Svg {...props}>{paths[name]}</Svg>,
  ]),
) as Record<IconName, (props: IconProps) => JSX.Element>;
